import React, { useState } from "react";
import { Link } from "react-router-dom";
import Tooltip from "@mui/material/Tooltip";
import { CircularProgress } from "@mui/material";
import {
  Public as GlobeIcon,
  OpenInNew as ViewIcon,
} from "@mui/icons-material";
import { useServices } from "../../services/Services";
import styles from "./SourcePill.module.css";
import { Osdk } from "@osdk/client";
import { Analysis } from "@unruly-osdk/sdk";
import { DeepDiveButton } from "./DeepDiveButton";
import { ShareButton } from "./ShareButton";

interface SourcePillProps {
  sourceUrl?: string;
  sourceLabel?: string;
  sourceName?: string;
  eventId: string;
  eventName?: string;
  eventSummary?: string;
  eventDate?: string;
  userId?: string;
  sourceNumber?: number;
  onOpenAnalysis?: (analysis: Osdk.Instance<Analysis>, eventId: string) => void;
  showShareDialog: (eventId: string) => void;
}

const SourcePill: React.FC<SourcePillProps> = ({
  sourceUrl,
  sourceLabel,
  sourceName,
  eventId,
  eventName,
  eventSummary,
  eventDate,
  userId,
  sourceNumber,
  onOpenAnalysis,
  showShareDialog,
}) => {
  const tooltipId = React.useId();
  const services = useServices();
  // State for controlling tooltip visibility
  const [tooltipOpen, setTooltipOpen] = useState(false);

  // Get existing request for this specific event
  const { data: existingRequest } = services.Request.useGetEventDeepDiveRequest(
    eventId,
    userId || "",
  );

  const getStatusIcon = () => {
    if (!existingRequest) {
      return null; // No icon if no deep-dive has been requested
    }
    // inlined style on width and height of the icons to avoid blown up icons in clipboard copy
    // css important does not work in this case, so we use inline style
    switch (existingRequest.status) {
      case "New":
        return (
          <GlobeIcon
            className={`${styles.statusIcon} ${styles.statusNew}`}
            style={{ width: "12px", height: "12px" }}
          />
        );
      case "Pending":
        return (
          <CircularProgress
            size={12}
            className={`${styles.statusIcon} ${styles.statusPending}`}
          />
        );
      case "Done":
        return (
          <GlobeIcon
            className={`${styles.statusIcon} ${styles.statusDone}`}
            style={{ width: "12px", height: "12px" }}
          />
        );
      case "Failed":
        return (
          <GlobeIcon
            className={`${styles.statusIcon} ${styles.statusFailed}`}
            style={{ width: "12px", height: "12px" }}
          />
        );
      default:
        return null;
    }
  };

  const tooltipContent = (
    <div className={styles.tooltipCard}>
      {eventName && <div className={styles.tooltipHeadline}>{eventName}</div>}
      <div className={styles.tooltipMetadata}>
        {eventDate && <span className={styles.tooltipDate}>{eventDate}</span>}
        {sourceName && (
          <>
            {eventDate && <span className={styles.tooltipSeparator}> • </span>}
            <span className={styles.tooltipSource}>Source: {sourceName}</span>
          </>
        )}
      </div>
      {eventSummary && (
        <div className={styles.tooltipSnippet}>
          {eventSummary.length > 120
            ? `${eventSummary.substring(0, 117)}...`
            : eventSummary}
        </div>
      )}
      <div className={styles.tooltipActions}>
        <div className={styles.tooltipButton}>
          <Link to={`/event/${eventId}`} className={styles.actionLink}>
            <div className={styles.actionIcon}>
              <ViewIcon
                fontSize="small"
                style={{ width: "16px", height: "16px" }}
              />
            </div>
            <span className={styles.actionLabel}>View</span>
          </Link>
        </div>
        <DeepDiveButton
          eventId={eventId}
          eventName={eventName}
          sourceUrl={sourceUrl}
          userId={userId}
          onOpenAnalysis={onOpenAnalysis}
        />
        <ShareButton onClick={() => showShareDialog(eventId)} />
      </div>
    </div>
  );
  const displayLabel = sourceLabel || sourceUrl || "No source";

  return (
    <Tooltip
      title={tooltipContent}
      placement="top"
      arrow
      open={tooltipOpen}
      onClose={() => setTooltipOpen(false)}
      onOpen={() => setTooltipOpen(true)}
      classes={{
        tooltip: styles.tooltip,
      }}
      id={`tooltip-${tooltipId}`}
    >
      <Link
        to={`/event/${eventId}`}
        className={`${styles.sourcePill} ${
          existingRequest?.status === "Done"
            ? styles.sourcePillCompleted
            : existingRequest?.status === "New"
              ? styles.sourcePillNew
              : existingRequest?.status === "Pending"
                ? styles.sourcePillPending
                : existingRequest?.status === "Failed"
                  ? styles.sourcePillFailed
                  : styles.sourcePillDefault
        }`}
      >
        {getStatusIcon()}
        {sourceNumber != null && <>{sourceNumber} &bull; </>}
        {displayLabel}
      </Link>
    </Tooltip>
  );
};

export default SourcePill;
